import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../components/Icon";
import * as api from "../api";
import { AG_TIPO_CLR, AG_ORIGENES } from "../constants";
import { equiposDe, diasDelPeriodo, fmtHours } from "../utils";

// En que anda ahora la gente. La misma pregunta que Asistencias —quien trabaja y
// cuando— pero a resolucion de minuto, asi que vive de pestaña vecina y no en
// Inicio, que es el panel personal de cada uno.
//
// Hay DOS fuentes, separadas y rotuladas, porque no responden lo mismo:
//
//   - El tablero de arriba, por departamentos, sale del FICHAJE de Odoo: quien
//     tiene la jornada abierta ahora mismo. Se pregunta con la sesion de quien
//     mira (routes/fichaje.js -> lib/presenciaOdoo.js), no hay usuario tecnico,
//     y de quien no se puede emparejar con una ficha de Odoo no se afirma nada:
//     sale como "sin dato", nunca como "fuera".
//   - Lo de abajo sale SOLO de los cronometros, que es dato nuestro y exacto, y
//     dice en que anda cada uno.
//
// Mezclarlas seria quedarse con lo peor de las dos: leer "no trabaja" donde solo
// dice "no usa el contador", o "esta fuera" donde dice "no lo sabemos".
//
// Y el que no cronometra no aparece como sospechoso: hay una columna aparte,
// rotulada "sin cronometrar", y a quien tiene una ausencia aprobada se le
// reconoce como ausente. Un tablero que confunde "no lo usa" con "no trabaja" se
// vuelve un instrumento de otra cosa.

const SIN_EQUIPO = "__sin__";

// Cada 45 s. El contador de cada fila corre solo desde `tramo_desde`, asi que no
// hace falta preguntar cada segundo para que los numeros vayan vivos.
const REFRESCO_MS = 45000;

const duracion = ms => {
  const t = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(t / 3600)).padStart(2, "0")}:`
       + `${String(Math.floor(t % 3600 / 60)).padStart(2, "0")}:`
       + `${String(t % 60).padStart(2, "0")}`;
};

const hoyMadrid = () =>
  new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Madrid" });

export default function ActividadView({ users = [], teams = [], currentUser,
                                        vacaciones = [], showToast = () => {} }) {
  const [datos,   setDatos]   = useState(null);
  const [fallo,   setFallo]   = useState(null);
  // El fichaje va aparte del cronometro a proposito: son dos fuentes distintas
  // y una puede fallar sin la otra. Si Odoo no contesta, el tablero de
  // cronometros sigue en pie y el panel de presencia dice por que esta vacio.
  const [pres,    setPres]    = useState(null);
  const [presMal, setPresMal] = useState(null);
  const [ahora,   setAhora]   = useState(Date.now());
  const [ultima,  setUltima]  = useState(null);
  const desfase = useRef(0);
  const desfasePres = useRef(0);
  const avisado = useRef(false);

  // Filtros de pantalla, el mismo idioma que Asistencias: primero el
  // departamento, y dentro de el quien. Los datos que llegan son los mismos.
  const [equiposOn,   setEquiposOn]   = useState(null);   // null = aun sin fijar
  const [personasOff, setPersonasOff] = useState(() => new Set());
  const [abiertoEq,   setAbiertoEq]   = useState(false);
  const [abiertoPer,  setAbiertoPer]  = useState(false);

  // ── Datos ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let vivo = true;
    const cargar = async () => {
      try {
        const d = await api.getActividadEquipo();
        if (!vivo) return;
        if (d.ahora) desfase.current = Date.parse(d.ahora) - Date.now();
        setDatos(d); setFallo(null); setUltima(Date.now());
        avisado.current = false;
      } catch (e) {
        if (!vivo) return;
        setFallo(e.message || "No se pudo leer la actividad");
        // Se avisa UNA vez: esto se refresca solo, y con el servidor caido
        // saldria un aviso cada 45 s tapando todo lo demas.
        if (!avisado.current) { avisado.current = true; showToast(e.message, "warn"); }
      }
    };
    // La presencia se pide en su propio viaje: pasa por Odoo y puede tardar o
    // fallar sola, y colgarla del mismo try dejaria sin cronometros por un
    // problema de fichaje.
    const cargarPresencia = async () => {
      try {
        const p = await api.getPresenciaEquipo();
        if (!vivo) return;
        if (p.ahora) desfasePres.current = Date.parse(p.ahora) - Date.now();
        setPres(p); setPresMal(null);
      } catch (e) {
        if (!vivo) return;
        // Sin toast: quien mira este tablero no ha pedido nada de Odoo, y este
        // panel ya explica en su sitio lo que le pasa.
        setPres(null);
        setPresMal({ msg: e.message || "No se pudo leer el fichaje", code: e.code });
      }
    };

    const cargar2 = () => { cargar(); cargarPresencia(); };
    cargar2();
    const id = setInterval(cargar2, REFRESCO_MS);
    const alVolver = () => { if (!document.hidden) cargar2(); };
    document.addEventListener("visibilitychange", alVolver);
    return () => {
      vivo = false; clearInterval(id);
      document.removeEventListener("visibilitychange", alVolver);
    };
  }, []); // eslint-disable-line

  // El tick de los contadores, local.
  useEffect(() => {
    const id = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const msDe = a => (a?.minutos_cerrados || 0) * 60000 +
    (a?.tramo_desde ? Math.max(0, ahora + desfase.current - Date.parse(a.tramo_desde)) : 0);

  // ── Equipos y personas ─────────────────────────────────────────────────────
  const miembrosDe = useMemo(() => {
    const m = {};
    teams.forEach(t => { m[String(t.id)] = new Set((t.members || []).map(String)); });
    return m;
  }, [teams]);

  const sinEquipo = useMemo(() => {
    const conEquipo = new Set();
    Object.values(miembrosDe).forEach(s => s.forEach(u => conEquipo.add(u)));
    return new Set(users.map(u => String(u.id)).filter(u => !conEquipo.has(u)));
  }, [users, miembrosDe]);

  const grupos = useMemo(() => [
    ...teams.map(t => ({ key: String(t.id), nombre: t.name, color: t.color })),
    ...(sinEquipo.size ? [{ key: SIN_EQUIPO, nombre: "Sin equipo", color: "#9ca3af" }] : []),
  ], [teams, sinEquipo]);

  // De entrada, los equipos propios: es lo que uno mira. Con la plantilla entera
  // delante no se lee nada, que es el mismo motivo que en Asistencias.
  useEffect(() => {
    if (equiposOn || !grupos.length) return;
    const mios = equiposDe(currentUser, teams).map(t => String(t.id));
    setEquiposOn(new Set(mios.length ? mios : grupos.map(g => g.key)));
  }, [grupos, equiposOn, currentUser, teams]);

  const enEquipoMarcado = uid => {
    if (!equiposOn) return true;
    // Alguien puede estar en varios equipos: basta con que uno este marcado.
    for (const t of teams)
      if (miembrosDe[String(t.id)]?.has(uid) && equiposOn.has(String(t.id))) return true;
    return sinEquipo.has(uid) ? equiposOn.has(SIN_EQUIPO) : false;
  };

  const etiquetaEquipo = uid => {
    const t = teams.find(x => miembrosDe[String(x.id)]?.has(uid));
    return t ? t.name : "Sin equipo";
  };

  // ── Ausencias de hoy ───────────────────────────────────────────────────────
  // Solo las APROBADAS: un borrador o una peticion denegada no eximen a nadie, y
  // darlas por buenas seria firmar lo que RRHH no ha firmado.
  const ausentes = useMemo(() => {
    const hoy = hoyMadrid();
    const m = new Map();
    vacaciones.forEach(v => {
      if (v.estado && v.estado !== "aprobada") return;
      if (!diasDelPeriodo(v.fecha, v.fecha_fin).includes(hoy)) return;
      m.set(String(v.user_id), v.tipo || "vacaciones");
    });
    return m;
  }, [vacaciones]);

  // ── Reparto en columnas ────────────────────────────────────────────────────
  const actividad = useMemo(() => {
    const m = new Map();
    (datos?.gente || []).forEach(g => m.set(String(g.user_id), g));
    return m;
  }, [datos]);

  const visibles = useMemo(() => users
    .filter(u => u.active !== false)
    .map(u => String(u.id))
    .filter(uid => enEquipoMarcado(uid) && !personasOff.has(uid)),
    [users, equiposOn, personasOff, miembrosDe, sinEquipo]); // eslint-disable-line

  const gentePorEquipo = useMemo(() => users
    .filter(u => u.active !== false)
    .map(u => ({ uid: String(u.id), nombre: u.name }))
    .filter(p => enEquipoMarcado(p.uid))
    .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    [users, equiposOn, miembrosDe, sinEquipo]); // eslint-disable-line

  const columnas = useMemo(() => {
    const trabajando = [], enPausa = [], sinNada = [], deBaja = [];
    visibles.forEach(uid => {
      const u = users.find(x => String(x.id) === uid);
      if (!u) return;
      const fila = { uid, nombre: u.name, equipo: etiquetaEquipo(uid), a: actividad.get(uid) };
      if (ausentes.has(uid))            deBaja.push({ ...fila, motivo: ausentes.get(uid) });
      else if (fila.a?.activo)          trabajando.push(fila);
      else if (fila.a?.pausados)        enPausa.push(fila);
      else                              sinNada.push(fila);
    });
    // El que lleva mas rato arriba: es lo que se busca de un vistazo.
    trabajando.sort((x, y) => msDe(y.a.activo) - msDe(x.a.activo));
    enPausa.sort((x, y) => (y.a?.minutos_hoy || 0) - (x.a?.minutos_hoy || 0));
    sinNada.sort((x, y) => x.nombre.localeCompare(y.nombre));
    return { trabajando, enPausa, sinNada, deBaja };
  }, [visibles, users, actividad, ausentes, ahora]); // eslint-disable-line

  const totalMin = columnas.trabajando.concat(columnas.enPausa)
    .reduce((s, f) => s + (f.a?.minutos_hoy || 0), 0);

  // ── Quien esta dentro, por departamento ────────────────────────────────────
  // Esto SI sale del fichaje: es la pregunta de "¿esta en su puesto?", que no es
  // la del cronometro. Se pinta en un tablero aparte para que no se confundan.
  const presenciaDe = useMemo(() => {
    const m = new Map();
    (pres?.gente || []).forEach(g => m.set(String(g.user_id), g));
    return m;
  }, [pres]);

  const msDentro = desde =>
    desde ? Math.max(0, ahora + desfasePres.current - Date.parse(desde)) : 0;

  const horaMadrid = iso => new Date(iso).toLocaleTimeString("es-ES",
    { timeZone: "Europe/Madrid", hour: "2-digit", minute: "2-digit" });

  const tablero = useMemo(() => {
    const cols = grupos
      .filter(g => !equiposOn || equiposOn.has(g.key))
      .map(g => {
        // Quien esta en dos departamentos sale en los dos: el tablero es por
        // departamento, no un reparto de personas en cajas.
        const miembros = visibles.filter(uid => g.key === SIN_EQUIPO
          ? sinEquipo.has(uid)
          : miembrosDe[g.key]?.has(uid));
        const filas = miembros.map(uid => {
          const u = users.find(x => String(x.id) === uid);
          return { uid, nombre: u?.name || uid,
                   p: presenciaDe.get(uid), a: actividad.get(uid),
                   ausencia: ausentes.get(uid) || null };
        });
        return {
          ...g,
          total:   miembros.length,
          // El que entro antes, primero: es el orden en que se ha ido llenando
          // la oficina y se lee de un tiron.
          dentro:  filas.filter(f => f.p?.estado === "dentro")
                        .sort((x, y) => Date.parse(x.p.desde || 0) - Date.parse(y.p.desde || 0)),
          // Sin ficha emparejada en Odoo no se afirma nada de esa persona.
          sinDato: filas.filter(f => !f.p).length,
        };
      })
      .filter(c => c.total > 0)
      .sort((x, y) => y.dentro.length - x.dentro.length || x.nombre.localeCompare(y.nombre));
    return cols;
  }, [grupos, equiposOn, visibles, users, miembrosDe, sinEquipo, presenciaDe, actividad, ausentes, ahora]); // eslint-disable-line

  const dentroTotal = useMemo(
    () => new Set(tablero.flatMap(c => c.dentro.map(f => f.uid))).size, [tablero]);
  const sinDatoTotal = useMemo(
    () => visibles.filter(uid => !presenciaDe.has(uid)).length, [visibles, presenciaDe]);

  // ── Estilos compartidos con la barra de Asistencias ────────────────────────
  const btnFiltro = on => ({
    display: "flex", alignItems: "center", gap: 7, padding: "6px 10px",
    borderRadius: 9, border: "1px solid var(--border)",
    background: on ? "var(--accent-bg)" : "var(--surface)",
    color: on ? "var(--accent)" : "var(--text2)",
    fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)",
  });
  const popover = {
    position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 41,
    width: 262, maxWidth: "calc(100vw - 24px)", background: "var(--surface)",
    borderRadius: 11, border: "1px solid var(--border)",
    boxShadow: "var(--shadow-md)", padding: "11px 12px",
  };
  const linkS = { background: "none", border: "none", padding: 0, cursor: "pointer",
                  fontFamily: "var(--font)", fontSize: 10, color: "var(--accent)" };
  const filaChk = { display: "flex", alignItems: "center", gap: 8, fontSize: 12,
                    cursor: "pointer", padding: "3px 0" };
  const card = { background: "var(--surface)", border: "1px solid var(--border)",
                 borderRadius: 13, boxShadow: "var(--shadow-sm)" };
  const tituloCol = { fontWeight: 700, fontSize: 10.5, textTransform: "uppercase",
                      letterSpacing: ".07em", display: "flex", alignItems: "center",
                      gap: 6, marginBottom: 10 };

  const nEqMarcados = equiposOn ? equiposOn.size : grupos.length;

  return (
    <div className="fu" style={{ display: "flex", flexDirection: "column", gap: 12,
      flex: 1, minHeight: 0, overflowY: "auto" }}>

      {/* Barra de filtros */}
      <div className="filters-bar bar-wrap" style={{ display: "flex", gap: 8, alignItems: "center",
        flexWrap: "wrap", flexShrink: 0 }}>

        {/* Equipos */}
        <div style={{ position: "relative" }}>
          <button aria-label="Qué departamentos salen" className="btn" onClick={() => setAbiertoEq(v => !v)}
            title="Qué departamentos salen" style={btnFiltro(nEqMarcados !== grupos.length)}>
            <Icon name="people-fill" size={11}
              color={nEqMarcados !== grupos.length ? "var(--accent)" : "var(--text2)"} />
            {nEqMarcados === grupos.length
              ? `${grupos.length} equipos`
              : `${nEqMarcados} de ${grupos.length} equipos`}
            <Icon name="chevron-down" size={9} color="var(--text3)" />
          </button>
          {abiertoEq && (
            <>
              <div onClick={() => setAbiertoEq(false)}
                style={{ position: "fixed", inset: 0, zIndex: 40 }} />
              <div style={popover}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8 }}>
                  <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: "var(--text3)",
                    textTransform: "uppercase", letterSpacing: ".1em" }}>Equipos</span>
                  <button className="btn" style={linkS}
                    onClick={() => setEquiposOn(new Set(grupos.map(g => g.key)))}>todos</button>
                  <span style={{ color: "var(--border)" }}>·</span>
                  <button className="btn" style={linkS}
                    onClick={() => setEquiposOn(new Set(equiposDe(currentUser, teams).map(t => String(t.id))))}>
                    los míos
                  </button>
                </div>
                <div style={{ maxHeight: 280, overflowY: "auto" }}>
                  {grupos.map(g => (
                    <label key={g.key} style={filaChk}>
                      <input type="checkbox" checked={!!equiposOn?.has(g.key)}
                        onChange={() => setEquiposOn(prev => {
                          const n = new Set(prev);
                          if (n.has(g.key)) n.delete(g.key); else n.add(g.key);
                          return n;
                        })}
                        style={{ accentColor: g.color || "var(--accent)", width: 14, height: 14,
                          cursor: "pointer", flexShrink: 0 }} />
                      <i style={{ width: 9, height: 9, borderRadius: 2,
                        background: g.color || "#9ca3af", flexShrink: 0 }} />
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis",
                        whiteSpace: "nowrap" }}>{g.nombre}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Personas, dentro de los equipos marcados. El de equipos elige
            departamentos; este elige quien, que son dos preguntas distintas. */}
        {gentePorEquipo.length > 0 && (
          <div style={{ position: "relative" }}>
            <button className="btn" onClick={() => setAbiertoPer(v => !v)}
              title="Quién sale en el tablero" style={btnFiltro(personasOff.size > 0)}>
              <Icon name="person-check-fill" size={11}
                color={personasOff.size ? "var(--accent)" : "var(--text2)"} />
              {personasOff.size
                ? `${visibles.length} de ${gentePorEquipo.length} personas`
                : `${gentePorEquipo.length} persona${gentePorEquipo.length === 1 ? "" : "s"}`}
              <Icon name="chevron-down" size={9} color="var(--text3)" />
            </button>
            {abiertoPer && (
              <>
                <div onClick={() => setAbiertoPer(false)}
                  style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                <div style={popover}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8 }}>
                    <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: "var(--text3)",
                      textTransform: "uppercase", letterSpacing: ".1em" }}>Personas</span>
                    <button className="btn" style={linkS}
                      onClick={() => setPersonasOff(new Set())}>todas</button>
                    <span style={{ color: "var(--border)" }}>·</span>
                    <button className="btn" style={linkS}
                      onClick={() => setPersonasOff(new Set(gentePorEquipo.map(p => p.uid)))}>ninguna</button>
                  </div>
                  <div style={{ maxHeight: 280, overflowY: "auto" }}>
                    {gentePorEquipo.map(p => (
                      <label key={p.uid} style={filaChk}>
                        <input type="checkbox" checked={!personasOff.has(p.uid)}
                          onChange={() => setPersonasOff(prev => {
                            const n = new Set(prev);
                            if (n.has(p.uid)) n.delete(p.uid); else n.add(p.uid);
                            return n;
                          })}
                          style={{ width: 14, height: 14, cursor: "pointer", flexShrink: 0 }} />
                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis",
                          whiteSpace: "nowrap" }}>{p.nombre}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10,
          fontSize: 11, color: "var(--text3)" }}>
          {totalMin > 0 && (
            <span>Cronometrado hoy <b style={{ color: "var(--accent)" }}>{fmtHours(totalMin / 60)}</b></span>
          )}
          <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <i style={{ width: 7, height: 7, borderRadius: "50%",
              background: fallo ? "var(--red)" : "var(--green)" }} />
            {fallo ? "sin conexión" : ultima ? "en vivo" : "cargando…"}
          </span>
        </div>
      </div>

      {/* ── Tablero de presencia, por departamentos ─────────────────────
          Este SI es el fichaje: quien tiene la jornada abierta en Odoo ahora
          mismo. Va arriba y separado del de cronometros porque son dos fuentes
          distintas, y mezclarlas es lo que hace que alguien lea "no trabaja"
          donde solo dice "no usa el contador". */}
      <div style={{ ...card, padding: 16, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          marginBottom: 12 }}>
          <Icon name="box-arrow-in-right" size={14} color="var(--ok-text)" />
          <span style={{ ...tituloCol, marginBottom: 0, color: "var(--text2)" }}>
            Dentro ahora mismo
          </span>
          <span style={{ fontSize: 10.5, color: "var(--text3)" }}>
            según el fichaje, por departamento
          </span>
          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center",
            gap: 10, fontSize: 11, color: "var(--text3)" }}>
            {pres && sinDatoTotal > 0 && (
              <span title="Personas sin ficha emparejada en Odoo: de ellas no se afirma nada">
                {sinDatoTotal} sin dato
              </span>
            )}
            <span>
              <b style={{ color: "var(--ok-text)", fontSize: 13 }}>{dentroTotal}</b>
              {` de ${visibles.length} dentro`}
            </span>
          </span>
        </div>

        {presMal ? (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 11.5,
            color: "var(--text3)", lineHeight: 1.5 }}>
            <Icon name="plug" size={12} color="var(--text3)" style={{ marginTop: 1 }} />
            <span>
              {presMal.code === "SIN_CONEXION"
                ? <>Esto se pregunta a Odoo con <b>tu</b> sesión: conecta tu cuenta en el
                    botón de fichaje y aquí saldrá quién está dentro.</>
                : presMal.msg}
            </span>
          </div>
        ) : !pres ? (
          <p style={{ fontSize: 12, color: "var(--text3)", margin: 0 }}>Preguntando a Odoo…</p>
        ) : !tablero.length ? (
          <p style={{ fontSize: 12, color: "var(--text3)", fontStyle: "italic", margin: 0 }}>
            No hay departamentos marcados
          </p>
        ) : (
          // Lienzo: los departamentos en columnas y se arrastra en horizontal. Con
          // 25 equipos no cabe en la pantalla de nadie, y apilarlos en vertical
          // hacia perder de vista justo la comparacion entre ellos.
          <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4,
            alignItems: "stretch" }}>
            {tablero.map(c => (
              <div key={c.key} style={{ flex: "0 0 218px", minWidth: 218,
                background: "var(--bg)", border: "1px solid var(--border)",
                borderRadius: 11, padding: 10, borderTop: `3px solid ${c.color || "#9ca3af"}`,
                display: "flex", flexDirection: "column", gap: 7 }}>

                <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <span style={{ flex: 1, fontSize: 11.5, fontWeight: 700, overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.nombre}>
                    {c.nombre}
                  </span>
                  <span style={{ fontSize: 10.5, fontWeight: 700,
                    color: c.dentro.length ? "var(--ok-text)" : "var(--text3)" }}>
                    {c.dentro.length}/{c.total}
                  </span>
                </div>

                {!c.dentro.length ? (
                  <span style={{ fontSize: 11, color: "var(--text3)", fontStyle: "italic" }}>
                    Nadie con jornada abierta
                  </span>
                ) : c.dentro.map(f => (
                  <div key={f.uid} style={{ background: "var(--surface)",
                    border: "1px solid var(--border)", borderRadius: 9, padding: "7px 9px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <i style={{ width: 6, height: 6, borderRadius: "50%",
                        background: "var(--green)", flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 12, fontWeight: 600, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.nombre}</span>
                      {/* Que además tenga el cronómetro en marcha se dice con un
                          icono y nada más: el que no lo use no es una falta. */}
                      {f.a?.activo && (
                        <Icon name="hourglass-split" size={10} color="var(--accent)"
                          style={{ opacity: .8 }} />
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 3,
                      fontSize: 10, color: "var(--text3)" }}>
                      <span>desde {f.p.desde ? horaMadrid(f.p.desde) : "?"}</span>
                      <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700,
                        color: "var(--ok-text)", fontVariantNumeric: "tabular-nums" }}>
                        {f.p.desde ? duracion(msDentro(f.p.desde)) : "—"}
                      </span>
                    </div>
                    {f.p.horas_hoy != null && (
                      <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>
                        {fmtHours(f.p.horas_hoy)} fichadas hoy
                      </div>
                    )}
                  </div>
                ))}

                {c.sinDato > 0 && (
                  <span style={{ fontSize: 9.5, color: "var(--text3)", marginTop: "auto" }}
                    title="No se ha podido emparejar su ficha de Odoo">
                    {c.sinDato} sin dato de fichaje
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {pres?.via === "hr.attendance" && (
          <div style={{ fontSize: 9.5, color: "var(--text3)", marginTop: 8 }}>
            Leído de las asistencias de Odoo con tu permiso de RRHH.
          </div>
        )}
      </div>

      {/* Aviso de lo que los tableros de abajo NO dicen. Va a la vista y no en un
          tooltip:
          quien mira tiene que saber que "sin cronometrar" no es "sin trabajar". */}
      <div style={{ ...card, padding: "9px 13px", display: "flex", alignItems: "flex-start",
        gap: 8, fontSize: 11, color: "var(--text3)", lineHeight: 1.5, flexShrink: 0 }}>
        <Icon name="lightbulb-fill" size={12} color="var(--text3)"
          style={{ marginTop: 1, flexShrink: 0 }} />
        <span>
          Lo que viene ahora sale del cronómetro del panel de inicio, no del fichaje.
          <b> «Sin cronometrar» no quiere decir sin trabajar</b>: quiere decir que esa
          persona no está usando el contador ahora mismo.
        </span>
      </div>

      {!datos && !fallo && (
        <div style={{ ...card, padding: 20, fontSize: 12, color: "var(--text3)" }}>
          Consultando la actividad…
        </div>
      )}

      {datos && (
        <div className="grid-1col-tab" style={{ display: "grid", gap: 12,
          gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr)", alignItems: "start" }}>

          {/* Trabajando ahora */}
          <div style={{ ...card, padding: 16 }}>
            <div style={{ ...tituloCol, color: "var(--text2)" }}>
              <Icon name="hourglass-split" size={13} color="var(--ok-text)" />
              Trabajando ahora
              <span style={{ marginLeft: "auto", color: "var(--ok-text)", fontWeight: 800,
                fontSize: 12 }}>{columnas.trabajando.length}</span>
            </div>
            {!columnas.trabajando.length ? (
              <p style={{ fontSize: 12, color: "var(--text3)", fontStyle: "italic", margin: 0 }}>
                Nadie tiene un cronómetro en marcha
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {columnas.trabajando.map(f => {
                  const a = f.a.activo;
                  const cl = AG_TIPO_CLR[a.type] || "var(--accent)";
                  const org = AG_ORIGENES[a.origen];
                  return (
                    <div key={f.uid} style={{ padding: "10px 12px", borderRadius: 10,
                      background: "var(--bg)", border: `1px solid color-mix(in srgb, ${cl} 45%, transparent)` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700, overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.nombre}</span>
                        <span style={{ fontSize: 9.5, color: "var(--text3)", flexShrink: 0 }}>
                          {f.equipo}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 800, color: cl, flexShrink: 0,
                          fontVariantNumeric: "tabular-nums" }}>{duracion(msDe(a))}</span>
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text)", marginTop: 3,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {a.description}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 4,
                        flexWrap: "wrap", fontSize: 10, color: "var(--text3)" }}>
                        <span style={{ color: cl, fontWeight: 700 }}>{a.type}</span>
                        {a.origen !== "ninguno" && org && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <Icon name={org.icon} size={9} color="var(--text3)" />
                            {a.etiqueta || org.label}
                            {a.project_name && ` · ${a.project_name}`}
                          </span>
                        )}
                        <span style={{ marginLeft: "auto" }}>
                          {fmtHours((f.a.minutos_hoy || 0) / 60)} hoy
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* El resto, en una columna mas estrecha: es contexto, no la respuesta */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

            {columnas.enPausa.length > 0 && (
              <div style={{ ...card, padding: 16 }}>
                <div style={{ ...tituloCol, color: "var(--text2)" }}>
                  <Icon name="pause-circle-fill" size={13} color="#f59e0b" />
                  En pausa
                  <span style={{ marginLeft: "auto", color: "#f59e0b", fontWeight: 800,
                    fontSize: 12 }}>{columnas.enPausa.length}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {columnas.enPausa.map(f => (
                    <div key={f.uid} style={{ display: "flex", alignItems: "center", gap: 8,
                      fontSize: 12, padding: "5px 8px", borderRadius: 8, background: "var(--bg)" }}>
                      <span style={{ flex: 1, fontWeight: 500, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.nombre}</span>
                      <span style={{ fontSize: 9.5, color: "var(--text3)" }}>{f.equipo}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b" }}>
                        {fmtHours((f.a.minutos_hoy || 0) / 60)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {columnas.deBaja.length > 0 && (
              <div style={{ ...card, padding: 16 }}>
                <div style={{ ...tituloCol, color: "var(--text2)" }}>
                  <Icon name="calendar-x" size={13} color="#8b5cf6" />
                  Ausentes hoy
                  <span style={{ marginLeft: "auto", color: "#8b5cf6", fontWeight: 800,
                    fontSize: 12 }}>{columnas.deBaja.length}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {columnas.deBaja.map(f => (
                    <div key={f.uid} style={{ display: "flex", alignItems: "center", gap: 8,
                      fontSize: 12, padding: "5px 8px", borderRadius: 8, background: "var(--bg)" }}>
                      <span style={{ flex: 1, fontWeight: 500, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.nombre}</span>
                      <span style={{ fontSize: 9.5, color: "#8b5cf6", textTransform: "capitalize" }}>
                        {f.motivo}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {columnas.sinNada.length > 0 && (
              <div style={{ ...card, padding: 16 }}>
                <div style={{ ...tituloCol, color: "var(--text2)" }}>
                  <Icon name="dash-lg" size={13} color="var(--text3)" />
                  Sin cronometrar
                  <span style={{ marginLeft: "auto", color: "var(--text3)", fontWeight: 800,
                    fontSize: 12 }}>{columnas.sinNada.length}</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {columnas.sinNada.map(f => (
                    <span key={f.uid} title={f.equipo}
                      style={{ fontSize: 11.5, padding: "3px 9px", borderRadius: 20,
                        background: "var(--bg)", color: "var(--text3)",
                        border: "1px solid var(--border2)" }}>{f.nombre}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
